import { getObjectSchema } from '../../Hasp/properties/v2/wireFormat.js';
import { hasRuntimeWritableMetadata, resolveRuntimeWireName } from '../../Hasp/properties/v2/runtime.js';
import { getGaugeNeedleBindableProperties } from '../../Hasp/gaugeNeedles.js';

export function formatOpenHASPPluginObjectRef(pageId, object, haspId = object?.haspid) {
  const prefix = object?.obj === 'connection' ? 'c' : 'b';
  return `p${pageId}${prefix}${haspId}`;
}

function hasBindingSource(binding) {
  if (binding.strategy === 'template') return Boolean(binding.template?.trim());
  if (binding.strategy === 'constant') return binding.value !== undefined && binding.value !== '';
  if (binding.strategy === 'switch') return Boolean(binding.entityId?.trim()) && binding.cases.length > 0;
  return Boolean(binding.entityId?.trim());
}

function segmentTarget(property) {
  if (!property?.startsWith('ring_segment.')) return null;
  const match = /^ring_segment\.([^.]+)\.value$/.exec(property);
  return match ? { id: match[1] } : { invalid: true };
}

/** Resolve existing Designer bindings to canonical openHASP plugin property keys. */
export function resolveOpenHASPPluginBindings(object, bindings) {
  const errors = [];
  const candidates = [];
  const segments = Array.isArray(object?.ring_segments) ? object.ring_segments : [];
  const segmentOrder = new Map(segments.map((segment, index) => [String(segment.id), index]));

  // Which properties a runtime transport may write is Schema V2's answer, not
  // the exporter's: every property carrying `runtime: { writable: true }`, and
  // nothing else. Schemas that declare no runtime metadata yet are left
  // unfiltered so migrating one object type at a time stays non-breaking.
  const schema = getObjectSchema(object?.obj);
  const enforceRuntimeWritable = hasRuntimeWritableMetadata(schema);

  for (const binding of bindings ?? []) {
    if (!binding?.property) continue;
    let property = binding.property;
    const isDynamicCollectionTarget =
      (object?.obj === 'badge' && property.startsWith('ring_segment.')) ||
      (object?.obj === 'gauge' && property.startsWith('needles['));

    if (enforceRuntimeWritable && !isDynamicCollectionTarget) {
      const wireName = resolveRuntimeWireName(schema, property);
      if (!wireName) {
        errors.push(`${object?.obj ?? 'object'} id=${object?.haspid}: property "${property}" is not runtime-writable in Schema V2.`);
        continue;
      }
      property = wireName;
    }

    if (object?.obj === 'gauge' && property.startsWith('needles[') &&
        !getGaugeNeedleBindableProperties(object).includes(property)) {
      errors.push(`gauge id=${object?.haspid}: property "${property}" is no longer an available needle target.`);
      continue;
    }

    const target = object?.obj === 'badge' ? segmentTarget(property) : null;
    if (target?.invalid) {
      errors.push(`Badge id=${object?.haspid}: invalid ring segment property "${property}".`);
      continue;
    }
    if (target) {
      if (object.ring_source !== 'manual') continue;
      if (!target.id || target.id.includes('.') || !segmentOrder.has(target.id)) {
        errors.push(`Badge id=${object?.haspid}: invalid ring segment id "${target.id}".`);
        continue;
      }
    }
    if (!hasBindingSource(binding)) {
      errors.push(`${object?.obj ?? 'object'} id=${object?.haspid}: property "${property}" has no concrete entity.`);
      continue;
    }
    candidates.push({ ...binding, property, _segmentOrder: target ? segmentOrder.get(target.id) : null });
  }

  const orderedSegments = candidates
    .filter(binding => binding._segmentOrder != null)
    .sort((a, b) => a._segmentOrder - b._segmentOrder);
  let segmentIndex = 0;
  const orderedCandidates = candidates.map(binding =>
    binding._segmentOrder == null ? binding : orderedSegments[segmentIndex++]);

  const seen = new Set();
  const resolved = [];
  for (const binding of orderedCandidates) {
    if (seen.has(binding.property)) {
      errors.push(`${object?.obj ?? 'object'} id=${object?.haspid}: duplicate plugin property "${binding.property}".`);
      continue;
    }
    seen.add(binding.property);
    const { _segmentOrder, ...clean } = binding;
    resolved.push(clean);
  }
  return { bindings: resolved, errors };
}
